// What tells a shop an order has arrived: the count that grew, the sound, the badge beside "Orders", and the title of
// a tab left in the background (S6 6e). Pure where it can be — scripts/check-orders-board.cjs calls these very
// functions — and every browser-only part guarded, so a browser with no sound and no storage still shows the board.
//
// AN ARRIVAL IS THE TOTAL GROWING. Every board answer, and the small counts answer, carry the whole table's count per
// stage whatever the view or the filter. A move changes two stages and never the total; a new order changes the
// total. So "the total grew by n" is n orders arrived — on any view, under any filter, without fetching a card. An
// order removed (a test's teardown) shrinks it: that is no arrival, only a new baseline.
export const totalOf = (counts) => Object.values(counts ?? {}).reduce((n, v) => (Number.isFinite(Number(v)) ? n + Number(v) : n), 0);
export const arrivalsBetween = (before, after) => (before == null || after == null ? 0 : Math.max(0, after - before));

// SEEN, PER PERSON AND PER BROWSER. The badge beside "Orders" counts what arrived since this browser last had the
// board in front of it. Kept in the browser as a convenience, never as a record: lost, the badge starts again from
// now. The first time a browser looks, everything already there is the baseline — never a backlog of "new".
export const seenKey = (userId) => `tl.orders.seen.${userId ?? "anon"}`;
const seenListeners = new Set();
export const readSeen = (userId) => {
  try {
    const v = globalThis.localStorage?.getItem(seenKey(userId));
    return v == null || v === "" || !Number.isFinite(Number(v)) ? null : Number(v);
  } catch { return null; }
};
export const markSeen = (userId, total) => {
  if (total == null || !Number.isFinite(Number(total))) return;
  try { globalThis.localStorage?.setItem(seenKey(userId), String(total)); } catch { /* a private window: the badge is per visit */ }
  for (const fn of seenListeners) fn(Number(total));
};
// The same tab hears it at once (a storage event only reaches OTHER tabs).
export const onSeen = (fn) => { seenListeners.add(fn); return () => { seenListeners.delete(fn); }; };
export const unseenOf = (total, seen) => (total == null || seen == null ? 0 : Math.max(0, total - seen));

// AN ORDER THAT CAME IN WHILE THE BOARD WAS OPEN, until someone has opened it or moved it. Measured against the
// server's own clock at the board's first answer, so a counter with a wrong clock still marks the right ones.
export const isFresh = (order, startedAt, handled) => {
  if (!startedAt || !order?.createdAt || handled?.has?.(order.id)) return false;
  const at = new Date(order.createdAt).getTime();
  return Number.isFinite(at) && at > new Date(startedAt).getTime();
};

// THE TAB'S TITLE, so a board in a background tab still says something came in.
export const titled = (base, n) => (n > 0 ? `(${n > 99 ? "99+" : n}) ${base}` : base);

// ── THE SOUND ────────────────────────────────────────────────────────────────
// Made, not shipped as a file: two rising notes, twice, from the browser's own synthesiser — nothing to download,
// nothing to license, and the same in every language. A browser lets a page make sound only after the person has
// touched it, so the board says so while sound is on and not yet allowed, rather than staying silent unnoticed.
const SOUND_KEY = "tl.orders.sound";
// On unless this browser was told otherwise: a new order nobody hears is the failure this exists to prevent.
export const soundWanted = () => { try { return globalThis.localStorage?.getItem(SOUND_KEY) !== "off"; } catch { return true; } };
export const setSoundWanted = (on) => { try { globalThis.localStorage?.setItem(SOUND_KEY, on ? "on" : "off"); } catch { /* per visit */ } };

export const CHIME_NOTES = Object.freeze([[880, 0], [1320, 0.16], [880, 0.5], [1320, 0.66]]);
// Any other page's own sound (the inbox's, inboxAlerts.js) is other notes through the same context — never a second context.
export function chime(ctx, notes = CHIME_NOTES, peak = 0.3) {
  if (!ctx || typeof ctx.createOscillator !== "function") return false;
  const t0 = ctx.currentTime + 0.02;
  for (const [freq, at] of notes) {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, t0 + at);
    gain.gain.setValueAtTime(0.0001, t0 + at);
    gain.gain.exponentialRampToValueAtTime(peak, t0 + at + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, t0 + at + 0.3);
    osc.connect(gain);
    gain.connect(ctx.destination);
    osc.start(t0 + at);
    osc.stop(t0 + at + 0.32);
  }
  return true;
}

// One sound context for the whole app, made only from a touch or a key — a context made before one starts blocked
// and earns a console warning in every browser.
let audio = null;
const AudioCtor = () => (typeof window !== "undefined" ? window.AudioContext ?? window.webkitAudioContext ?? null : null);
export const soundSupported = () => !!AudioCtor();
export const soundAllowed = () => audio?.state === "running";
export const allowSound = async () => {
  const Ctor = AudioCtor();
  if (!Ctor) return false;
  try {
    if (!audio) audio = new Ctor();
    if (audio.state !== "running") await audio.resume();
  } catch { /* still blocked; the next touch tries again */ }
  return audio?.state === "running";
};
export const playChime = () => (soundAllowed() ? chime(audio) : false);
export const playNotes = (notes, peak) => (soundAllowed() ? chime(audio, notes, peak) : false);

// The first touch or key anywhere on the page lets sound start. Returns the way to stop listening.
export function armSoundOnGesture(onReady) {
  if (typeof document === "undefined" || !soundSupported() || soundAllowed()) return () => {};
  const off = () => {
    document.removeEventListener("pointerdown", go, true);
    document.removeEventListener("keydown", go, true);
  };
  async function go() { if (await allowSound()) { off(); onReady?.(); } }
  document.addEventListener("pointerdown", go, true);
  document.addEventListener("keydown", go, true);
  return off;
}
